import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Autocomplete, Radio, TextField, createFilterOptions, Accordion, AccordionSummary, AccordionDetails, useTheme } from '@mui/material';
import useIsMobile from '../../../hooks/useIsMobile';
import OptimizedCityAutocomplete from '../../../components/OptimizedCityAutocomplete';
import GooglePlacesAutocomplete from '../../../components/GooglePlacesAutocomplete';
import { useSelector, useDispatch } from 'react-redux';
import { getClients } from '../../../store/slices/clientsSlice';
import { getActiveAdmins, getActiveDispatchers, getTechnicians } from '../../../store/slices/usersSlice';
import { isOwner, isGeneralManager, isManagement, canSeePrivateAttachment, canEditJobNotes as canEditJobNotesPermission } from '../../../utils/permissions';
import { formatUserDisplayName } from '../../../utils/userDisplayName';
import { isTechnicianAssignable, isTechnicianArchived } from '../../../utils/technicianStatus';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import { getUser } from '../../../store/slices/userSlice';
import { useEffect } from 'react';
import { api, states } from '../../../utils/constants';
import { POST, UPLOAD } from '../../../configs/api';
import {
  ACCEPT_ATTACHMENTS,
  isVideoFile,
  isPreviewableMedia,
  isUploadCancelledError,
  uploadViaPresignedPost,
  validateAttachmentFile,
} from '../../../utils/fileTypes';
import { getAllStatuses, getCreatableStatusOptions } from '../../../utils/jobStatuses';
import { PRIORITY_OPTIONS } from '../../../utils/jobPriorities';
import {
  getMinimumSlaInputValue,
  getSlaPayloadValue,
  getSlaValidationMessage,
} from '../../../utils/jobSla';
import { fetchStoreNameSuggestions, addStoreNameSuggestion, removeStoreNameSuggestion, upsertStoreNameOption } from '../../../utils/storeNameSuggestions';
import { fetchJobTags } from '../../../utils/jobTagSuggestions';
import JobTagsField from './JobTagsField';
import { getModernFieldSurface, getModernTextFieldSx } from './modernFormFieldStyles';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { addJob } from '../../../store/slices/jobsSlice';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VideocamIcon from '@mui/icons-material/Videocam';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import TableChartIcon from '@mui/icons-material/TableChart';
import DownloadIcon from '@mui/icons-material/Download';
import LinearProgress from '@mui/material/LinearProgress';
import AddTechnicianDialog from './AddTechnicianDialog';
import JobLinksSection, { saveJobLinksForJob } from './JobLinksSection';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

// Helper function to get display name for technician/vendor
const getTechnicianDisplayName = (tech) => {
  if (!tech) return '';
  if (tech.type === 'vendor') {
    return tech.company_name || '';
  }
  return `${tech.first_name || ''} ${tech.last_name || ''}`.trim();
};

function PreviewPaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-preview-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

// Zip code formatting function
const formatZipCode = (value) => {
  // Remove all non-numeric characters
  const zipCode = value.replace(/\D/g, '');
  
  // Format as 12345-6789 (5+4 format)
  if (zipCode.length > 5) {
    const firstPart = zipCode.substring(0, 5);
    const secondPart = zipCode.substring(5, 9);
    return `${firstPart}-${secondPart}`;
  }
  return zipCode;
};

// Attachment categories - mapping between display name and database value
// Note: quickbooks_invoice is intentionally excluded as it should not be displayed
const ATTACHMENT_CATEGORIES_MAP = {
  'Before': 'Before',
  'After': 'After', 
  'Sign off': 'Sign off',
  'from_client': 'From client',
  'Other': 'Other'
};

const ATTACHMENT_CATEGORIES = Object.keys(ATTACHMENT_CATEGORIES_MAP);

// Helper to get display name from database value
const getCategoryDisplayName = (dbValue) => ATTACHMENT_CATEGORIES_MAP[dbValue] || dbValue;

const emptyJob = {client: '', admin: '', dispatcher: '', status: 'Open', priority: 'P24', sla: '', state: '', city: '', description: '', address: '', address_2: '', trade: '', store_name: '', zip_code: '', latitude: null, longitude: null, nte: 0, work_order: '', notes: ''};

export default function AddJobDialog({ setAddJobDialogOpen, addJobDialogOpen }) {
  const theme = useTheme();
  const isSmallScreen = useIsMobile();
  const isDarkMode = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const { clients } = useSelector(getClients);
  const [newJob, setNewJob] = React.useState(emptyJob);
  const admins = useSelector(getActiveAdmins);
  const dispatchers = useSelector(getActiveDispatchers);
  const technicians = useSelector(getTechnicians);
  const user = useSelector(getUser);
  const canPickAdmin = isOwner(user) || isGeneralManager(user);
  const canEditNotesField = canEditJobNotesPermission(user);
  const canTogglePrivacy = canSeePrivateAttachment(user);
  const [technicianAssignments, setTechnicianAssignments] = React.useState([]);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogValue, setDialogValue] = React.useState({ name: '' });
  const [loading, setLoading] = React.useState(false);
  const [showError, setShowError] = React.useState('');
  const [errors, setErrors] = React.useState({});
  const [showSuccessToast, setShowSuccessToast] = React.useState(false);
  const [dateErrors, setDateErrors] = React.useState({});
  const [technicianInputValue, setTechnicianInputValue] = React.useState('');
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState({});
  const [imagePreviewOpen, setImagePreviewOpen] = React.useState(false);
  const [previewImage, setPreviewImage] = React.useState(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadStatus, setUploadStatus] = React.useState('');
  const [uploadErrors, setUploadErrors] = React.useState({});
  const [createdJobId, setCreatedJobId] = React.useState(null);
  const [pendingJobLinks, setPendingJobLinks] = React.useState([]);
  const attachmentsSectionRef = React.useRef(null);
  const dialogContentRef = React.useRef(null);
  const pendingScrollToErrorRef = React.useRef(false);
  const uploadAbortRef = React.useRef(null); // AbortController for in-flight video uploads
  const [storeNameOptions, setStoreNameOptions] = React.useState([]);
  const [tagOptions, setTagOptions] = React.useState([]);
  const [selectedTags, setSelectedTags] = React.useState([]);
  const canManageStoreNames = isManagement(user);
  const canCreateJobTags = isManagement(user);
  const canDeleteJobTags = isManagement(user);
  const creatableStatuses = React.useMemo(() => getCreatableStatusOptions(), []);
  const activeClients = React.useMemo(
    () => (clients || []).filter((client) => Number(client.client_status) === 1),
    [clients],
  );
  const filteredAdmins = React.useMemo(() => {
    if (!admins || !newJob?.client?.id) return [];
    return admins.filter((a) => a.client_ids?.includes(newJob.client.id));
  }, [admins, newJob?.client?.id]);

  React.useEffect(() => {
    if (!pendingScrollToErrorRef.current || Object.keys(errors).length === 0) return;
    pendingScrollToErrorRef.current = false;

    const container = dialogContentRef.current;
    if (!container) return;

    const errorEl = container.querySelector('.MuiFormControl-root.Mui-error, .Mui-error');
    if (!errorEl) return;

    const target =
      errorEl.closest('.MuiAutocomplete-root') ||
      errorEl.closest('.MuiFormControl-root') ||
      errorEl;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [errors]);

  const validateJob = () => {
    let newErrors = {};
    if (!newJob?.work_order || !String(newJob.work_order).trim()) {
      newErrors.work_order = 'Work order is required';
    } else if (String(newJob.work_order).trim().length > 100) {
      newErrors.work_order = 'Work order must not exceed 100 characters';
    }
    if (!newJob.client) newErrors.client = 'Client is required';
    if (!newJob?.trade) newErrors.trade = 'Trade is required';
    if (!newJob?.store_name || !String(newJob.store_name).trim()) {
      newErrors.store_name = 'Store name is required';
    } else if (String(newJob.store_name).trim().length > 255) {
      newErrors.store_name = 'Store name must not exceed 255 characters';
    }
    if (!newJob?.description || newJob?.description?.trim().length === 0) {
      newErrors.description = 'Description is required';
    }
    if (newJob?.nte === '' || newJob?.nte === null || newJob?.nte === undefined) {
      newErrors.nte = 'NTE is required';
    } else if (Number.isNaN(parseFloat(newJob.nte)) || parseFloat(newJob.nte) < 0) {
      newErrors.nte = 'NTE must be a non-negative number';
    }
    if (canPickAdmin && !newJob?.admin) newErrors.admin = 'Admin is required';
    if (!newJob?.dispatcher) newErrors.dispatcher = 'Dispatcher is required';
    if (!newJob?.status || newJob?.status?.length === 0) newErrors.status = 'Status is required';
    if (!newJob?.priority || newJob?.priority?.length === 0) newErrors.priority = 'Priority is required';
    if (!newJob?.state || newJob?.state?.length === 0) newErrors.state = 'State is required';
    if (!newJob?.city || newJob?.city?.length === 0) newErrors.city = 'City is required';
    if (!newJob?.address || newJob?.address?.length < 3) newErrors.address = 'Address must be at least 3 characters';
    if (newJob?.address_2 && newJob.address_2.length > 500) newErrors.address_2 = 'Address line 2 must not exceed 500 characters';
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(newJob?.zip_code)) {
      newErrors.zip_code = 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)';
    }
    if (newJob?.notes != null && newJob.notes.length > 500) {
      newErrors.notes = 'Notes must not exceed 500 characters';
    }
    const slaError = getSlaValidationMessage({ value: newJob?.sla, required: true });
    if (slaError) {
      newErrors.sla = slaError;
    }
    
    if (Object.keys(newErrors).length === 0) {
      setShowError('');
      setErrors({});
      return true;
    }

    setShowError('Please correct the highlighted fields');
    setErrors(newErrors);
    pendingScrollToErrorRef.current = true;
    return false;
  };

  // Technician assignment helper functions
  const addTechnicianAssignment = (technician) => {
    const newAssignment = {
      id: crypto.randomUUID(),
      technician: technician,
      tech_cost: '',
      paid_date: '',
      scheduled_date: '',
      status: 1, // Default to Active
      notes: ''
    };
    setTechnicianAssignments(prev => [...prev, newAssignment]);
  };

  const removeTechnicianAssignment = (assignmentId) => {
    setTechnicianAssignments(prev => prev.filter(assignment => assignment.id !== assignmentId));
  };

  const updateTechnicianAssignment = (assignmentId, field, value) => {
    // Validate dates to prevent past dates
    if (field === 'scheduled_date' || field === 'paid_date') {
      if (value && new Date(value) < new Date(new Date().setHours(0, 0, 0, 0))) {
        // Show error for this specific field
        setDateErrors(prev => ({
          ...prev,
          [`${assignmentId}-${field}`]: 'Date cannot be in the past'
        }));
        return; // Don't update if date is in the past
      } else {
        // Clear error if date is valid
        setDateErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`${assignmentId}-${field}`];
          return newErrors;
        });
      }
    }
    
    setTechnicianAssignments(prev => prev.map(assignment => 
      assignment.id === assignmentId ? { ...assignment, [field]: value } : assignment
    ));
  };

  const handleDialogClose = () => {
    setDialogValue({ name: '' });
    setDialogOpen(false);
    setTechnicianInputValue('');
  };

  // Handle place selection from Google Places Autocomplete
  const handlePlaceSelect = (addressData) => {
    // Format zip code if provided, otherwise clear it
    const formattedZip = (addressData.zipCode && addressData.zipCode.trim()) 
      ? formatZipCode(addressData.zipCode) 
      : '';
    
    setNewJob({
      ...newJob,
      address: addressData.address || newJob.address,
      // Clear city if not provided, otherwise use the provided value
      city: (addressData.city && addressData.city.trim()) ? addressData.city : '',
      // Clear state if not provided, otherwise use the provided value
      state: (addressData.state && addressData.state.trim()) ? addressData.state : '',
      zip_code: formattedZip,
      // Store coordinates if available
      latitude: addressData.latitude !== null && addressData.latitude !== undefined ? addressData.latitude : null,
      longitude: addressData.longitude !== null && addressData.longitude !== undefined ? addressData.longitude : null,
    });

    setErrors((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      if (addressData.address || newJob.address) delete next.address;
      if (addressData.city && addressData.city.trim()) delete next.city;
      if (addressData.state && addressData.state.trim()) delete next.state;
      if (formattedZip) delete next.zip_code;
      return next;
    });
  };

  const handleTechnicianAdded = (technician) => {
    // Add the newly created technician as an assignment
    addTechnicianAssignment(technician);
    handleDialogClose();
  };

  const getStatusValue = (status) => {
    // Find status by label and return its ID
    const statusObj = getAllStatuses().find(s => s.label === status);
    return statusObj ? statusObj.id : 1; // Default to 1 (Open) if not found
  };

  const getPriorityValue = (priority) => {
    if (priority === 'EMG P4') return 1;
    if (priority === 'P7') return 2;
    if (priority === 'P24') return 3;
    if (priority === 'P48') return 4;
    return 2;
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return null; // Will show image preview
    } else if (fileType.startsWith('video/')) {
      return <VideocamIcon sx={{ fontSize: 40, color: '#9c27b0' }} />;
    } else if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon sx={{ fontSize: 40, color: '#dc3545' }} />;
    } else if (fileType.includes('word') || fileType.includes('document')) {
      return <DescriptionIcon sx={{ fontSize: 40, color: '#1976d2' }} />;
    } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
      return <TableChartIcon sx={{ fontSize: 40, color: '#28a745' }} />;
    } else if (fileType.startsWith('text/')) {
      return <TextSnippetIcon sx={{ fontSize: 40, color: '#6c757d' }} />;
    } else {
      return <UploadFileIcon sx={{ fontSize: 40, color: '#666' }} />;
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    addFiles(files);
  };

  const getEffectiveFilePrivacy = (fileObj) => (
    canTogglePrivacy ? fileObj.is_private === true : false
  );

  const addFiles = (files) => {
    const rejected = [];
    const accepted = [];

    files.forEach(file => {
      const validationError = validateAttachmentFile(file);
      if (validationError) rejected.push(validationError);
      else accepted.push(file);
    });

    const newFiles = accepted.map(file => ({
      id: crypto.randomUUID(),
      file: file,
      preview: isPreviewableMedia(file.type) ? URL.createObjectURL(file) : null,
      category: 'Before', // Default category for job creation
      is_private: false, // public by default; users with permission can switch to private
    }));
    setSelectedFiles(prev => [...prev, ...newFiles]);

    if (rejected.length) {
      setShowError(rejected.join('\n'));
    }
  };

  // Update file privacy setting
  const updateFilePrivacy = (fileId, isPrivate) => {
    setSelectedFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, is_private: isPrivate } : f
    ));
    // Clear validation error for this file
    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fileId];
      return newErrors;
    });
  };

  // Update file category
  const updateFileCategory = (fileId, category) => {
    setSelectedFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, category } : f
    ));
  };

  const removeFile = (fileId) => {
    setSelectedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
    // Clear any upload errors for this file
    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fileId];
      return newErrors;
    });
  };

  /**
   * Upload one staged file to a created job. Videos bypass the API and go
   * straight to S3 with a presigned POST; everything else posts to /uploads/direct.
   */
  const uploadSingleFile = async (fileObj, jobId, signal) => {
    if (isVideoFile(fileObj.file.type)) {
      const { error, message, cancelled } = await uploadViaPresignedPost(
        fileObj.file,
        {
          entityType: 'job',
          entityId: jobId,
          fileName: fileObj.file.name,
          category: fileObj.category || 'Before',
          isPrivate: getEffectiveFilePrivacy(fileObj),
        },
        (percent) => setUploadProgress(prev => ({
          ...prev,
          [fileObj.id]: { progress: percent, success: undefined },
        })),
        { signal },
      );
      return {
        success: !error,
        cancelled: !!cancelled || isUploadCancelledError({ message, cancelled }),
        message,
      };
    }

    if (signal?.aborted) {
      return { success: false, cancelled: true, message: 'Upload cancelled' };
    }

    const formData = new FormData();
    formData.append('file', fileObj.file);
    formData.append('entityType', 'job');
    formData.append('entityId', jobId);
    formData.append('category', fileObj.category || 'Before');
    formData.append('is_private', getEffectiveFilePrivacy(fileObj));

    const { status, message } = await UPLOAD(api.uploadJobFiles, formData);
    return { success: status === 200 || status === 201, message };
  };

  const handleCancelUpload = () => {
    uploadAbortRef.current?.abort();
  };

  const retryUpload = async (fileId, jobId) => {
    const fileObj = selectedFiles.find(f => f.id === fileId);
    if (!fileObj) return;

    const abortController = new AbortController();
    uploadAbortRef.current = abortController;
    setIsUploading(true);
    setUploadStatus(`Retrying upload for ${fileObj.file.name}...`);
    
    try {
      const { success, message, cancelled } = await uploadSingleFile(fileObj, jobId, abortController.signal);
      
      if (success) {
        setUploadStatus(`Successfully uploaded ${fileObj.file.name}`);
        // Clear error for this file
        setUploadErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fileId];
          return newErrors;
        });
        // Update progress to show success
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { progress: 100, success: true }
        }));
      } else if (cancelled) {
        setUploadStatus('Upload cancelled.');
        setUploadErrors(prev => ({
          ...prev,
          [fileId]: 'Upload cancelled'
        }));
      } else {
        setUploadStatus(`Failed to upload ${fileObj.file.name}`);
        setUploadErrors(prev => ({
          ...prev,
          [fileId]: message || 'Upload failed - please try again'
        }));
      }
    } catch (error) {
      console.error('Retry upload error:', error);
      setUploadStatus(`Error uploading ${fileObj.file.name}`);
      setUploadErrors(prev => ({
        ...prev,
        [fileId]: `Upload error: ${error.message || 'Unknown error'}`
      }));
    } finally {
      uploadAbortRef.current = null;
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const uploadFiles = async (jobId) => {
    const abortController = new AbortController();
    uploadAbortRef.current = abortController;
    setIsUploading(true);
    setUploadStatus('Uploading files...');
    setUploadErrors({}); // Clear previous errors
    
    const totalFiles = selectedFiles.length;
    let completedFiles = 0;
    const results = [];
    let cancelled = false;
    
    for (let i = 0; i < selectedFiles.length; i++) {
      if (abortController.signal.aborted) {
        cancelled = true;
        break;
      }

      const fileObj = selectedFiles[i];
      setUploadStatus(`Uploading ${fileObj.file.name}...`);
      
      try {
        const { success, message, cancelled: wasCancelled } = await uploadSingleFile(
          fileObj,
          jobId,
          abortController.signal,
        );

        if (wasCancelled) {
          cancelled = true;
          results.push(false);
          setUploadErrors(prev => ({
            ...prev,
            [fileObj.id]: 'Upload cancelled'
          }));
          break;
        }

        results.push(success);
        
        completedFiles++;
        const progress = (completedFiles / totalFiles) * 100;
        setUploadProgress(prev => ({
          ...prev,
          [fileObj.id]: { progress, success }
        }));
        
        if (success) {
          setUploadStatus(`Uploaded ${fileObj.file.name} (${completedFiles}/${totalFiles})`);
          // Clear any previous error for this file
          setUploadErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[fileObj.id];
            return newErrors;
          });
        } else {
          setUploadStatus(`Failed to upload ${fileObj.file.name}`);
          setUploadErrors(prev => ({
            ...prev,
            [fileObj.id]: message || 'Upload failed - please try again'
          }));
        }
      } catch (error) {
        console.error('Upload error:', error);
        results.push(false);
        completedFiles++;
        setUploadStatus(`Error uploading ${fileObj.file.name}`);
        setUploadErrors(prev => ({
          ...prev,
          [fileObj.id]: `Upload error: ${error.message || 'Unknown error'}`
        }));
      }
    }
    
    uploadAbortRef.current = null;
    setIsUploading(false);
    setUploadStatus(cancelled ? 'Upload cancelled.' : 'Upload complete');
    
    // Clear progress after a short delay only if all uploads succeeded
    const allSuccessful = results.length === totalFiles && results.every(result => result);
    if (allSuccessful) {
      setTimeout(() => {
        setUploadProgress({});
        setUploadStatus('');
        setUploadErrors({}); // Clear all upload errors when all uploads succeed
      }, 2000);
    }
    
    return results;
  };

  const handleFilePreview = async (fileObj) => {
    const previewData = {
      src: fileObj.preview || URL.createObjectURL(fileObj.file),
      name: fileObj.file.name,
      size: fileObj.file.size,
      type: fileObj.file.type,
      textContent: null
    };

    // Read text content for text files
    if (fileObj.file.type.startsWith('text/')) {
      try {
        const text = await fileObj.file.text();
        previewData.textContent = text.substring(0, 1000) + (text.length > 1000 ? '...' : '');
      } catch (error) {
        console.error('Error reading text file:', error);
        previewData.textContent = 'Error reading file content';
      }
    }

    setPreviewImage(previewData);
    setImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImage(null);
  };

  const handleClear = () => {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
    setNewJob(emptyJob);
    setTechnicianAssignments([]);
    setDateErrors({});
    setTechnicianInputValue('');
    setErrors({});
    setShowError('');
    // Clean up file previews
    selectedFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
    setSelectedFiles([]);
    // Reset upload states
    setUploadProgress({});
    setIsUploading(false);
    setUploadStatus('');
    setUploadErrors({});
    setCreatedJobId(null);
    setPendingJobLinks([]);
    setSelectedTags([]);
  }

  const handleAddJob = async () => {
    if (!validateJob()) return;
    
    // Scroll to attachments section if files or links are present
    if ((selectedFiles.length > 0 || pendingJobLinks.length > 0) && attachmentsSectionRef.current) {
      attachmentsSectionRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
    
    setLoading(true);
    
    let body = {
      ...newJob, 
      client_id: newJob.client.id,
      status: getStatusValue(newJob.status),
      priority: getPriorityValue(newJob.priority),
      trade: newJob.trade,
      store_name: newJob.store_name.trim(),
      zip_code: newJob.zip_code || '',
      latitude: newJob.latitude || null,
      longitude: newJob.longitude || null,
      nte: typeof newJob.nte === 'number' ? newJob.nte : (parseFloat(newJob.nte) || 0),
      work_order: newJob.work_order.trim(),
      notes: (newJob.notes && newJob.notes.trim()) ? newJob.notes.trim().slice(0, 500) : null,
      sla: getSlaPayloadValue(newJob.sla),
      dispatcher_id: newJob.dispatcher.id,
      tag_ids: selectedTags.map((tag) => tag.id),
      technicianAssignments: technicianAssignments.map(assignment => ({
        technician_id: assignment.technician.id,
        technician_name: getTechnicianDisplayName(assignment.technician),
        tech_cost: assignment.tech_cost || 0,
        paid_date: assignment.paid_date || null,
        scheduled_date: assignment.scheduled_date || null,
        status: assignment.status || 1,
        notes: assignment.notes || ''
      })),
    };

    if (newJob?.admin) body.admin_id = newJob.admin.id;

    const result = await POST(api.createJob, {}, body);
    const { status, data, message: errorMessage } = result;

    if (status === 201) {
      setCreatedJobId(data.id); // Store the job ID for retry functionality

      const finishJobCreation = () => {
        setShowSuccessToast(true);
        setNewJob(emptyJob);
        setTechnicianAssignments([]);
        selectedFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
        setSelectedFiles([]);
        setPendingJobLinks([]);
        setSelectedTags([]);
        const newAddedJob = { ...data, client_name: newJob.client.client_name };
        if (!canPickAdmin) {
          newAddedJob.admin = user.email || '';
        } else {
          newAddedJob.admin = newJob.admin?.email || '';
        }
        if (newJob?.dispatcher) {
          newAddedJob.dispatcher = newJob.dispatcher.email || '';
        }
        dispatch(addJob(newAddedJob));
        setAddJobDialogOpen(false);
      };

      let failedLinks = 0;
      if (pendingJobLinks.length > 0) {
        failedLinks = await saveJobLinksForJob(data.id, pendingJobLinks);
      }

      if (selectedFiles.length > 0) {
        const uploadResults = await uploadFiles(data.id);
        const failedUploads = uploadResults.filter(result => !result);

        if (failedUploads.length > 0 || failedLinks > 0) {
          const parts = [];
          if (failedUploads.length > 0) {
            parts.push(`${failedUploads.length} file(s) failed to upload`);
          }
          if (failedLinks > 0) {
            parts.push(`${failedLinks} link(s) failed to save`);
          }
          setShowError(`Work order created successfully, but ${parts.join(' and ')}. Please try again.`);
          setLoading(false);
          return;
        }

        setTimeout(finishJobCreation, 1000);
      } else if (failedLinks > 0) {
        setShowError(
          `Work order created successfully, but ${failedLinks} link(s) failed to save. Please add them from the job details.`
        );
        setLoading(false);
        return;
      } else {
        finishJobCreation();
      }
    } else {
      setShowError(errorMessage || data?.message || 'Something went wrong, please try again later.');
      if (data?.errors && Array.isArray(data.errors)) {
        const fieldErrors = {};
        data.errors.forEach((err) => {
          let field = err.param || err.path;
          // API uses client_id; the form field key is client.
          if (field === 'client_id') field = 'client';
          if (field) fieldErrors[field] = err.msg || (field === 'work_order' ? 'This work order is already assigned to another job' : err.msg);
        });
        if (Object.keys(fieldErrors).length > 0) setErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
    }
    setLoading(false);
  };

  // Clear only the validation banner once all field errors are resolved
  useEffect(() => {
    if (Object.keys(errors).length === 0) {
      setShowError((current) => (
        current === 'Please correct the highlighted fields' ? '' : current
      ));
    }
  }, [errors]);

  const clearFieldError = React.useCallback((field) => {
    setErrors((prev) => {
      if (!prev?.[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // Load shared store name and tag suggestions when dialog opens
  useEffect(() => {
    if (!addJobDialogOpen) {
      setSelectedTags([]);
      return;
    }
    let cancelled = false;
    fetchStoreNameSuggestions().then((options) => {
      if (!cancelled) setStoreNameOptions(options);
    });
    fetchJobTags({ activeOnly: true }).then((options) => {
      if (!cancelled) setTagOptions(options);
    });
    return () => { cancelled = true; };
  }, [addJobDialogOpen]);

  // Theme-driven option styling to keep dropdown text readable in dark mode.
  const fieldSurface = getModernFieldSurface(isDarkMode);
  const optionTextColor = theme.palette.text.primary;
  const optionTextColorSelected = fieldSurface.accent;
  const optionBgSelected = theme.palette.action.selected;
  const optionBgHover = theme.palette.action.hover;

  // Shared modern outlined field styles (same look as AI / proposal fields).
  const textFieldSx = getModernTextFieldSx(isDarkMode);
  const autocompleteSx = getModernTextFieldSx(isDarkMode, {
    '& .MuiAutocomplete-listbox': {
      borderRadius: '12px',
      border: `1px solid ${fieldSurface.panelBorder}`,
      backgroundColor: fieldSurface.fieldBg,
      padding: 0,
    },
  });

  return (
    <Dialog
      open={addJobDialogOpen}
      onClose={() => setAddJobDialogOpen(false)}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            maxHeight: isSmallScreen ? '100%' : '90vh',
            m: isSmallScreen ? 0 : undefined,
          }
        }
      }}
    >
      {/* Header */}
      <Box 
        id="draggable-dialog-title" 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          p: { xs: 2, md: 3 }, 
          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0', 
          cursor: isSmallScreen ? 'default' : 'move',
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
          New Work Order
        </Typography>
        <IconButton 
          onClick={() => setAddJobDialogOpen(false)} 
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: isDarkMode ? '#1e293b' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* New Work Form */}
      <DialogContent ref={dialogContentRef} sx={{ p: 3 }}>
        {showError && (
          <Alert
            severity="error"
            variant="outlined"
            onClose={() => setShowError('')}
            sx={{
              mb: 2,
              borderRadius: 2,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(6px)',
              '& .MuiAlert-message': {
                fontWeight: 500,
                fontSize: '0.9rem',
              },
            }}
          >
            {showError}
          </Alert>
        )}
        {/* Main Form Fields - Vertical Layout */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          {/* Work Order */}
          <TextField
              label="Work Order (Required)"
              value={newJob.work_order || ''}
              onChange={(e) => {
                setNewJob({ ...newJob, work_order: e.target.value });
                clearFieldError('work_order');
              }}
              placeholder="Enter work order..."
              error={!!errors.work_order}
              helperText={errors.work_order}
              fullWidth
              sx={textFieldSx}
              slotProps={{ input: { maxLength: 100 } }}
            />
          {/* Client Selection */}
          <Autocomplete
            options={activeClients}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (!option) return '';
              return option.client_name || '';
            }}
            value={newJob?.client || null}
            onChange={(_, value) => {
              setNewJob({ ...newJob, client: value, admin: '' });
              clearFieldError('client');
            }}
            sx={autocompleteSx}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li 
                  key={key} 
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: '12px 16px',
                    margin: 0,
                    borderBottom: isDarkMode ? '1px solid #334155' : '1px solid rgba(0, 0, 0, 0.04)',
                    background: selected 
                      ? (isDarkMode ? '#1e293b' : '#ececec')
                      : 'transparent',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = selected 
                      ? (isDarkMode ? '#1e293b' : '#ececec')
                      : (isDarkMode ? '#1e293b' : '#f8f9fa');
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = selected 
                      ? (isDarkMode ? '#1e293b' : '#ececec')
                      : 'transparent';
                  }}
                >
                  <Radio
                    checked={selected}
                    sx={{ 
                      marginRight: 2,
                      padding: '6px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.1rem',
                        color: selected ? fieldSurface.accent : (isDarkMode ? '#94a3b8' : '#9e9e9e'),
                        transition: 'all 0.2s ease-in-out',
                      },
                      '&:hover .MuiSvgIcon-root': {
                        color: fieldSurface.accent,
                        transform: 'scale(1.1)',
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column',flex: 1}}>
                    <Typography variant="body1" sx={{ fontWeight: selected ? 600 : 500,color: selected ? fieldSurface.accent : 'text.primary',fontSize: '0.95rem',transition: 'all 0.2s ease-in-out',}}>
                      {option.client_name || ''}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" sx={{ color: 'text.secondary',fontSize: '0.8rem',marginTop: '2px'}}>
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Client (Required)" 
                fullWidth 
                error={errors.client} 
                helperText={errors.client}
                sx={textFieldSx}
              />
            )}
          />

          {/* Trade Selection */}
          <Autocomplete 
            options={['Plumbing', 'HVAC', 'Electrical', 'Handyman', 'Doors', 'Pest Control', 'Locksmith', 'Appliance', 'Glass']} 
            value={newJob.trade}
            onChange={(event, value) => {
              setNewJob({ ...newJob, trade: value });
              clearFieldError('trade');
            }}
            sx={autocompleteSx}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li 
                  key={key} 
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: '12px 16px',
                    margin: 0,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                    background: selected 
                      ? optionBgSelected
                      : 'transparent',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    ':hover': {
                      background: selected 
                        ? optionBgSelected
                        : optionBgHover,
                    }
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: selected ? 600 : 500,
                      color: selected ? optionTextColorSelected : optionTextColor,
                      fontSize: '0.95rem',
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {option}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Trade (Required)" 
                error={!!errors.trade}
                helperText={errors.trade}
                fullWidth
                sx={textFieldSx}
              />
            )}
          />

          {/* Store name */}
          <Autocomplete
            freeSolo
            options={storeNameOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            value={newJob.store_name || ''}
            onChange={async (event, value) => {
              const str = typeof value === 'string' ? value : (value?.name ?? '');
              setNewJob({ ...newJob, store_name: str });
              clearFieldError('store_name');
              if (str && str.trim() && canManageStoreNames && !storeNameOptions.some((option) => option.name === str.trim())) {
                const created = await addStoreNameSuggestion(str.trim());
                if (created) {
                  setStoreNameOptions((prev) => upsertStoreNameOption(prev, created));
                }
              }
            }}
            onInputChange={(event, value) => {
              setNewJob({ ...newJob, store_name: value ?? '' });
              clearFieldError('store_name');
            }}
            sx={autocompleteSx}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              const name = typeof option === 'string' ? option : option.name;
              const optionId = typeof option === 'string' ? null : option.id;
              return (
                <li
                  key={key}
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: '12px 16px',
                    margin: 0,
                    borderBottom: isDarkMode ? '1px solid #334155' : '1px solid rgba(0, 0, 0, 0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.95rem' }}>
                    {name}
                  </Typography>
                  {canManageStoreNames && optionId && (
                    <IconButton
                      size="small"
                      onClick={async (e) => {
                        e.stopPropagation();
                        const removed = await removeStoreNameSuggestion(optionId);
                        if (removed) {
                          setStoreNameOptions((prev) => prev.filter((item) => item.id !== optionId));
                        }
                      }}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      aria-label={`Remove "${name}" from suggestions`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Store Name (Required)"
                error={!!errors.store_name}
                helperText={errors.store_name}
                fullWidth
                sx={textFieldSx}
              />
            )}
          />

          {/* Job Address */}
          <GooglePlacesAutocomplete
            label="Address (Required)"
            value={newJob.address || ''}
            onChange={(value) => {
              setNewJob({ ...newJob, address: value });
              clearFieldError('address');
            }}
            onPlaceSelect={handlePlaceSelect}
            error={!!errors.address}
            helperText={errors.address}
            placeholder=""
          />

          <TextField
            label="Address Line 2 (Optional)"
            value={newJob.address_2 || ''}
            onChange={(e) => {
              setNewJob({ ...newJob, address_2: e.target.value });
              clearFieldError('address_2');
            }}
            error={!!errors.address_2}
            helperText={errors.address_2}
            placeholder="Suite, unit, floor, etc."
            fullWidth
            sx={textFieldSx}
          />

          {/* City, State, and Zip Code Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Job City */}
            <OptimizedCityAutocomplete
              value={newJob.city || ''}
              onChange={(value) => {
                setNewJob({ ...newJob, city: value });
                clearFieldError('city');
              }}
              sx={{ flex: 2, ...autocompleteSx }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="City (Required)" 
                  error={errors.city} 
                  helperText={errors.city}
                  sx={textFieldSx}
                />
              )}
            />

            {/* Job State */}
            <Autocomplete 
              options={Object.keys(states)} 
              value={newJob.state}
              onChange={(event, value) => {
                setNewJob({ ...newJob, state: value });
                clearFieldError('state');
              }}
              getOptionLabel={(option) => option ? `${states[option]} - ${option}` : ''}
              sx={{ flex: 1.5, ...autocompleteSx }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li 
                    key={key} 
                    {...optionProps}
                    style={{
                      ...optionProps.style,
                      padding: '12px 16px',
                      margin: 0,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                      background: selected 
                      ? optionBgSelected
                        : 'transparent',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      ':hover': {
                        background: selected 
                        ? optionBgSelected
                        : optionBgHover,
                      }
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: selected ? 600 : 500,
                      color: selected ? optionTextColorSelected : optionTextColor,
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {states[option]} - {option}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="State (Required)" 
                  error={errors.state} 
                  helperText={errors.state}
                  sx={textFieldSx}
                />
              )}
            />

            {/* Job Zip Code */}
            <TextField
              label="Zip Code (Required)"
              value={newJob.zip_code || ''}
              onChange={(event) => {
                const formattedZip = formatZipCode(event.target.value);
                setNewJob({ ...newJob, zip_code: formattedZip });
                clearFieldError('zip_code');
              }}
              onBlur={() => {
                // Validate zip code on blur for real-time feedback
                const zipRegex = /^\d{5}(-\d{4})?$/;
                if (newJob.zip_code && !zipRegex.test(newJob.zip_code)) {
                  setErrors(prev => ({
                    ...prev,
                    zip_code: 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)'
                  }));
                } else if (newJob.zip_code && zipRegex.test(newJob.zip_code)) {
                  clearFieldError('zip_code');
                }
              }}
              placeholder="12345 or 12345-6789"
              error={!!errors.zip_code}
              helperText={errors.zip_code}
              sx={{ flex: 1, ...textFieldSx }}
            />
          </Box>

          {/* Job Description */}
          <TextField
            multiline
            label="Description (Required)"
            placeholder="Describe the work that needs to be done..."
            value={newJob.description}
            onChange={(e) => {
              setNewJob({ ...newJob, description: e.target.value });
              clearFieldError('description');
            }}
            error={errors.description}
            helperText={errors.description}
            rows={3}
            fullWidth
            sx={textFieldSx}
          />

          {/* NTE */}
          <TextField
              label="NTE (Required)"
              type="number"
              value={newJob.nte === '' ? '' : newJob.nte}
              onChange={(e) => {
                setNewJob({ ...newJob, nte: e.target.value === '' ? '' : e.target.value });
                clearFieldError('nte');
              }}
              onWheel={(e) => e.target.blur()}
              error={!!errors.nte}
              helperText={errors.nte}
              fullWidth
              sx={textFieldSx}
              slotProps={{
                input: {
                  startAdornment: <Box sx={{ color: 'text.secondary', fontWeight: 700, mr: 0.5 }}>$</Box>,
                },
                htmlInput: {
                  step: '0.01',
                  min: 0,
                },
              }}
            />
          {/* Admin Selection */}
          {canPickAdmin && (
            <Autocomplete
              options={filteredAdmins}
              disabled={!newJob?.client?.id}
              getOptionLabel={(option) => {
                if (!option) return '';
                if (typeof option === 'string') return option;
                return formatUserDisplayName(option) || option.email || '';
              }}
              value={newJob?.admin || null}
              onChange={(_, value) => {
                setNewJob({ ...newJob, admin: value });
                clearFieldError('admin');
              }}
              sx={autocompleteSx}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li 
                    key={key} 
                    {...optionProps}
                    style={{
                      ...optionProps.style,
                      padding: '12px 16px',
                      margin: 0,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                      background: selected 
                        ? optionBgSelected
                        : 'transparent',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      ':hover': {
                        background: selected 
                          ? optionBgSelected
                          : optionBgHover,
                      }
                    }}
                  >
                    <Radio 
                      checked={selected}
                      sx={{ 
                        marginRight: 2,
                        padding: '6px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.1rem',
                          color: selected ? fieldSurface.accent : (isDarkMode ? '#94a3b8' : '#9e9e9e'),
                          transition: 'all 0.2s ease-in-out',
                        },
                        '&:hover .MuiSvgIcon-root': {
                          color: fieldSurface.accent,
                          transform: 'scale(1.1)',
                        }
                      }}
                    />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: selected ? 600 : 500,
                        color: selected ? optionTextColorSelected : optionTextColor,
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {typeof option === 'string' ? option : formatUserDisplayName(option) || option.email || ''}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Admin (Required)" 
                  error={!!errors.admin}
                  helperText={
                    !newJob?.client?.id
                      ? 'Select a client first'
                      : errors.admin
                  }
                  fullWidth
                  sx={textFieldSx}
                />
              )}
            />
          )}

          {/* Dispatchers */}
          <Autocomplete 
            options={dispatchers || []}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (typeof option === 'string') return option;
              return formatUserDisplayName(option) || option.email || '';
            }}
            value={newJob?.dispatcher}
            onChange={(_, value) => {
              setNewJob({ ...newJob, dispatcher: value });
              clearFieldError('dispatcher');
            }}
            sx={autocompleteSx}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li 
                  key={key} 
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: '12px 16px',
                    margin: 0,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                    background: selected 
                      ? optionBgSelected
                      : 'transparent',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    ':hover': {
                      background: selected 
                        ? optionBgSelected
                        : optionBgHover,
                    }
                  }}
                >
                  <Radio 
                    checked={selected}
                    sx={{ 
                      marginRight: 2,
                      padding: '6px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.1rem',
                        color: selected ? fieldSurface.accent : (isDarkMode ? '#94a3b8' : '#9e9e9e'),
                        transition: 'all 0.2s ease-in-out',
                      },
                      '&:hover .MuiSvgIcon-root': {
                        color: fieldSurface.accent,
                        transform: 'scale(1.1)',
                      }
                    }}
                  />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: selected ? 600 : 500,
                      color: selected ? optionTextColorSelected : optionTextColor,
                      fontSize: '0.95rem',
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {typeof option === 'string' ? option : formatUserDisplayName(option) || option.email || ''}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Dispatcher (Required)" 
                error={!!errors.dispatcher}
                helperText={errors.dispatcher}
                fullWidth
                sx={textFieldSx}
              />
            )}
          />

          {/* Job Priority and SLA */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Autocomplete 
              options={PRIORITY_OPTIONS.map((o) => o.label)} 
              value={newJob.priority}
              onChange={(event, value) => {
                setNewJob({ ...newJob, priority: value });
                clearFieldError('priority');
              }}
              disableClearable
              sx={{ ...autocompleteSx, flex: 1 }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li 
                    key={key} 
                    {...optionProps}
                    style={{
                      ...optionProps.style,
                      padding: '12px 16px',
                      margin: 0,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                      background: selected 
                        ? optionBgSelected
                        : 'transparent',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      ':hover': {
                        background: selected 
                          ? optionBgSelected
                          : optionBgHover,
                      }
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: selected ? 600 : 500,
                        color: selected ? optionTextColorSelected : optionTextColor,
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {option}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Priority (Required)" 
                  error={errors.priority} 
                  helperText={errors.priority}
                  fullWidth
                  sx={textFieldSx}
                />
              )}
            />
            <TextField
              label="SLA (Required)"
              type="datetime-local"
              value={newJob.sla || ''}
              onChange={(e) => {
                setNewJob({ ...newJob, sla: e.target.value });
                clearFieldError('sla');
              }}
              error={!!errors.sla}
              helperText={errors.sla || 'Must be 30+ minutes in the future.'}
              sx={{ ...textFieldSx, flex: 1 }}
              slotProps={{
                input: {
                  min: getMinimumSlaInputValue()
                },
                inputLabel: {
                  shrink: true
                }
              }}
            />
          </Box>

          {/* Technicians */}
          <Autocomplete 
            value={null}
            inputValue={technicianInputValue}
            onInputChange={(event, newInputValue) => {
              setTechnicianInputValue(newInputValue);
            }}
            onChange={(event, value) => {
              if (value) {
                if (value && typeof value === 'object' && !isTechnicianAssignable(value)) {
                  setTechnicianInputValue('');
                  return;
                }
                // Check if it's the "Add {name}" option object
                if (value && typeof value === 'object' && value.inputValue) {
                  setDialogOpen(true);
                  setDialogValue({ name: value.inputValue });
                } else {
                  // Add technician as assignment
                  addTechnicianAssignment(value);
                  setTechnicianInputValue('');
                }
              }
            }}
            filterOptions={(options, params) => {
              const filtered = createFilterOptions()(options, params);
              const { inputValue } = params;
              const isExisting = options.some((option) => {
                if (option && typeof option === 'object') {
                  return inputValue === getTechnicianDisplayName(option);
                }
                return false;
              });
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  label: `Add "${inputValue}"`,
                });
              }
              return filtered;
            }}
            options={technicians || []}
            getOptionLabel={(option) => {
              if (option.inputValue) {
                return option.inputValue;
              }
              // Handle user objects - return the user's name (or company name for vendors)
              if (option && typeof option === 'object') {
                return getTechnicianDisplayName(option);
              }
              return option;
            }}
            isOptionEqualToValue={(option, value) => {
              // Handle user objects
              if (option && typeof option === 'object' && value && typeof value === 'object') {
                return option.id === value.id;
              }
              return false;
            }}
            isOptionDisabled={(option) => (
              option && typeof option === 'object' && !isTechnicianAssignable(option)
            )}
            sx={autocompleteSx}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              const isBlacklisted = option && typeof option === 'object' && option.blacklisted === true;
              const isArchived = option && typeof option === 'object' && isTechnicianArchived(option);
              const isUnavailable = isBlacklisted || isArchived;
              return (
                <li 
                  key={key} 
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: '12px 16px',
                    margin: 0,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                    background: isUnavailable
                      ? '#ffebee' 
                      : (selected ? optionBgSelected : 'transparent'),
                    borderLeft: isBlacklisted ? '4px solid #d32f2f' : isArchived ? '4px solid #9e9e9e' : 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    ':hover': {
                      background: isUnavailable
                        ? '#ffcdd2' 
                        : (selected ? optionBgSelected : optionBgHover),
                    }
                  }}
                >
                  <Radio 
                    checked={selected}
                    disabled={isUnavailable}
                    sx={{ 
                      marginRight: 2,
                      padding: '6px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.1rem',
                        color: isUnavailable ? '#9e9e9e' : (selected ? fieldSurface.accent : '#9e9e9e'),
                        transition: 'all 0.2s ease-in-out',
                      },
                      '&:hover .MuiSvgIcon-root': {
                        color: isUnavailable ? '#9e9e9e' : fieldSurface.accent,
                        transform: isUnavailable ? 'none' : 'scale(1.1)',
                      }
                    }}
                  />
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: selected ? 600 : 500,
                      color: isUnavailable ? '#757575' : (selected ? optionTextColorSelected : optionTextColor),
                          fontSize: '0.95rem',
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        {option.label || getTechnicianDisplayName(option) || option}
                      </Typography>
                      {isBlacklisted && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            backgroundColor: '#d32f2f',
                            color: '#fff',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Blacklisted
                        </Box>
                      )}
                      {isArchived && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            backgroundColor: '#9e9e9e',
                            color: '#fff',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Archived
                        </Box>
                      )}
                    </Box>
                    {isBlacklisted && option.blocked_reason && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#d32f2f',
                          fontSize: '0.75rem',
                          mt: 0.5,
                          fontStyle: 'italic',
                        }}
                      >
                        Reason: {option.blocked_reason}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            selectOnFocus 
            clearOnBlur 
            handleHomeEndKeys
            autoHighlight
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Add Technician (Optional)" 
                placeholder="Select or add technician" 
                fullWidth
                sx={textFieldSx}
              />
            )}
          />

          {/* Technician Assignments */}
          {technicianAssignments.map((assignment, index) => {
            const technicianName = getTechnicianDisplayName(assignment.technician);
            const isBlacklisted = assignment.technician?.blacklisted === true;
            // job_technicians.status display labels (separate from jobs.status)
            const getTechStatusLabel = (status) => {
              if (status === 1) return 'Dispatched';
              if (status === 2) return 'On route';
              if (status === 3) return 'Start';
              if (status === 4) return 'Paid';
              if (status === 5) return 'Request payment';
              if (status === 6) return 'No show';
              if (status === 7) return 'Reschedule';
              if (status === 8) return 'Need part';
              if (status === 9) return 'Free of charge';
              return 'Dispatched';
            };
            
            return (
              <Accordion 
                key={assignment.id}
                defaultExpanded={index === 0}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: isBlacklisted ? '0 2px 8px rgba(211, 47, 47, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: isBlacklisted ? '2px solid #d32f2f' : 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: '0 0 16px 0',
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: isBlacklisted 
                      ? (isDarkMode ? '#7f1d1d' : '#ffebee')
                      : 'background.paper',
                    borderRadius: '8px 8px 0 0',
                    '&.Mui-expanded': {
                      borderRadius: '8px 8px 0 0',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: isBlacklisted ? '#d32f2f' : 'text.primary' }}>
                        {technicianName} {technicianAssignments.filter(a => a.technician.id === assignment.technician.id).length > 1 && 
                          `(${technicianAssignments.filter(a => a.technician.id === assignment.technician.id).indexOf(assignment) + 1})`}
                      </Typography>
                      {isBlacklisted && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: '#d32f2f',
                            color: '#fff',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Blacklisted
                        </Box>
                      )}
                    </Box>
                    <IconButton
                      component="div"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTechnicianAssignment(assignment.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        removeTechnicianAssignment(assignment.id);
                      }}
                      size="small"
                      sx={{ color: '#dc3545' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ 
                  p: 3, 
                  backgroundColor: isBlacklisted 
                    ? (isDarkMode ? '#7f1d1d' : '#ffebee') 
                    : 'background.paper'
                }}>
                  {isBlacklisted && assignment.technician?.blocked_reason && (
                    <Box sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      backgroundColor: '#ffcdd2', 
                      borderRadius: 1, 
                      border: '1px solid #d32f2f'
                    }}>
                      <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.75rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                        ⚠️ Blacklisted Reason:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#b71c1c', fontSize: '0.875rem' }}>
                        {assignment.technician.blocked_reason}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Status and Scheduled Date Row */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Autocomplete 
                        options={[
                          { value: 1, label: 'Dispatched' },
                          { value: 2, label: 'On route' },
                          { value: 3, label: 'Start' },
                          { value: 4, label: 'Paid' },
                          { value: 5, label: 'Request payment' },
                          { value: 6, label: 'No show' },
                          { value: 7, label: 'Reschedule' },
                          { value: 8, label: 'Need part' }
                        ]}
                        value={{ value: assignment.status, label: getTechStatusLabel(assignment.status) }}
                        onChange={(event, value) => updateTechnicianAssignment(assignment.id, 'status', value?.value || 1)}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        renderInput={(params) => <TextField {...params} label="Status" sx={textFieldSx} />}
                        sx={{ flex: 1, ...autocompleteSx }}
                      />
                      <TextField 
                        label="Scheduled Date" 
                        type="datetime-local" 
                        value={assignment.scheduled_date || ''} 
                        onChange={(e) => updateTechnicianAssignment(assignment.id, 'scheduled_date', e.target.value)}
                        error={!!dateErrors[`${assignment.id}-scheduled_date`]}
                        helperText={dateErrors[`${assignment.id}-scheduled_date`]}
                        sx={{ flex: 1, ...textFieldSx }}
                        slotProps={{
                          input: {
                            min: new Date().toISOString().slice(0, 16)
                          },
                          inputLabel: {
                            shrink: true
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Tech Cost and Paid Date Row */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField 
                        label="Tech Cost" 
                        type="number" 
                        value={assignment.tech_cost || ''} 
                        onChange={(e) => updateTechnicianAssignment(assignment.id, 'tech_cost', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        sx={{ flex: 1, ...textFieldSx }}
                        slotProps={{
                          input: {
                            startAdornment: <Box sx={{ color: fieldSurface.muted, fontWeight: 700, mr: 0.5 }}>$</Box>,
                          },
                          htmlInput: {
                            min: 0,
                            step: '0.01',
                          },
                        }}
                      />
                      <TextField 
                        label="Paid Date" 
                        type="datetime-local" 
                        value={assignment.paid_date || ''} 
                        onChange={(e) => updateTechnicianAssignment(assignment.id, 'paid_date', e.target.value)}
                        error={!!dateErrors[`${assignment.id}-paid_date`]}
                        helperText={dateErrors[`${assignment.id}-paid_date`]}
                        sx={{ flex: 1, ...textFieldSx }}
                        slotProps={{
                          input: {
                            min: new Date().toISOString().slice(0, 16)
                          },
                          inputLabel: {
                            shrink: true
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Notes */}
                    <TextField 
                      label="Notes" 
                      multiline 
                      rows={3} 
                      value={assignment.notes || ''} 
                      onChange={(e) => updateTechnicianAssignment(assignment.id, 'notes', e.target.value)}
                      placeholder="Add notes for this technician assignment..."
                      sx={textFieldSx}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Job Status */}
          <Autocomplete 
            options={creatableStatuses.map((status) => status.label)}
            value={newJob.status}
            onChange={(event, value) => {
              setNewJob({ ...newJob, status: value });
              clearFieldError('status');
            }}
            disableClearable
            sx={autocompleteSx}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li 
                  key={key} 
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: '12px 16px',
                    margin: 0,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                    background: selected 
                      ? optionBgSelected
                      : 'transparent',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    ':hover': {
                      background: selected 
                        ? optionBgSelected
                        : optionBgHover,
                    }
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: selected ? 600 : 500,
                      color: selected ? optionTextColorSelected : optionTextColor,
                      fontSize: '0.95rem',
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {option}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Status" 
                error={errors.status} 
                helperText={errors.status}
                fullWidth
                sx={textFieldSx}
              />
            )}
          />

          <JobTagsField
            label="Tags (Optional)"
            value={selectedTags}
            onChange={setSelectedTags}
            tagOptions={tagOptions}
            setTagOptions={setTagOptions}
            canCreateTags={canCreateJobTags}
            canDeleteTags={canDeleteJobTags}
            sx={autocompleteSx}
          />

          {/* Attachments Section */}
          <Box ref={attachmentsSectionRef} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Attachments (Optional)
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Links and photos (optional). Select photos, files, blueprints etc. to upload to this work order from your device.
            </Typography>

            <JobLinksSection
              links={pendingJobLinks}
              onLinksChange={setPendingJobLinks}
              user={user}
              onError={setShowError}
              isDraft
            />
            
            <Box 
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{ 
                border: isDragging ? `2px solid ${fieldSurface.accent}` : `2px dashed ${fieldSurface.panelBorder}`, 
                borderRadius: '12px', 
                p: 4, 
                textAlign: 'center',
                backgroundColor: isDragging 
                  ? (isDarkMode ? 'rgba(30, 41, 59, 0.55)' : 'rgba(248, 250, 252, 0.95)') 
                  : fieldSurface.fieldBg,
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <UploadFileIcon sx={{ fontSize: 48, color: isDragging ? fieldSurface.accent : 'text.secondary', mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1, color: 'text.primary' }}>
                Drag and drop files here, or click to browse
              </Typography>
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="file-upload-input"
                accept={ACCEPT_ATTACHMENTS}
              />
              <label htmlFor="file-upload-input">
                <Button 
                  component="span"
                  variant="contained" 
                  sx={{ 
                    backgroundColor: isDarkMode ? '#fff' : '#000', 
                    color: isDarkMode ? '#000' : 'white',
                    '&:hover': { 
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333'
                    }
                  }}
                >
                  Browse Files
                </Button>
              </label>
            </Box>
            
            {/* Upload Status */}
            {isUploading && (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.55)' : 'rgba(248, 250, 252, 0.95)', 
                borderRadius: '12px', 
                border: `1px solid ${fieldSurface.panelBorder}` 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ color: fieldSurface.accent, fontWeight: 600 }}>
                    {uploadStatus}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleCancelUpload}
                    sx={{ textTransform: 'none', color: '#d32f2f', minWidth: 'auto' }}
                  >
                    Cancel upload
                  </Button>
                </Box>
                <LinearProgress 
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(15, 23, 42, 0.08)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: fieldSurface.accent
                    }
                  }}
                />
              </Box>
            )}

            {/* File Preview List */}
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Selected Files ({selectedFiles.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedFiles.map((fileObj) => (
                    <Box 
                      key={fileObj.id}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        p: 2, 
                        border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: 'background.paper'
                      }}
                    >
                      <Box
                        onClick={() => handleFilePreview(fileObj)}
                        sx={{
                          width: 40,
                          height: 40,
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        {fileObj.preview && isVideoFile(fileObj.file.type) ? (
                          <>
                            <video
                              src={fileObj.preview}
                              muted
                              playsInline
                              preload="metadata"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: 4,
                                backgroundColor: '#000'
                              }}
                            />
                            <PlayCircleOutlineIcon
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: 'rgba(255,255,255,0.95)',
                                pointerEvents: 'none',
                                fontSize: '22px !important',
                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.65))',
                              }}
                            />
                          </>
                        ) : fileObj.preview ? (
                          <img 
                            src={fileObj.preview} 
                            alt={fileObj.file.name}
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover', 
                              borderRadius: 4
                            }}
                          />
                        ) : (
                          getFileIcon(fileObj.file.type)
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {fileObj.file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {(fileObj.file.size / 1024).toFixed(2)} KB
                        </Typography>
                        {/* Category Selection */}
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            Category
                          </Typography>
                          <Autocomplete
                            size="small"
                            options={ATTACHMENT_CATEGORIES}
                            getOptionLabel={(option) => getCategoryDisplayName(option)}
                            value={fileObj.category || 'Before'}
                            onChange={(event, newValue) => updateFileCategory(fileObj.id, newValue || 'Before')}
                            disableClearable
                            sx={{ minWidth: 150 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select category"
                                sx={{ 
                                  '& .MuiInputBase-root': { fontSize: '0.75rem' }
                                }}
                              />
                            )}
                          />
                        </Box>
                        {/* Privacy toggle only for users who can mark attachments private */}
                        {canTogglePrivacy && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                              Privacy
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant={fileObj.is_private === false ? 'contained' : 'outlined'}
                                onClick={() => updateFilePrivacy(fileObj.id, false)}
                                sx={{
                                  minWidth: 'auto',
                                  px: 2,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  ...(fileObj.is_private === false ? {
                                    backgroundColor: '#4caf50',
                                    '&:hover': { backgroundColor: '#388e3c' }
                                  } : {
                                    borderColor: '#4caf50',
                                    color: '#4caf50',
                                    '&:hover': { borderColor: '#388e3c', backgroundColor: 'rgba(76, 175, 80, 0.08)' }
                                  })
                                }}
                              >
                                Public
                              </Button>
                              <Button
                                size="small"
                                variant={fileObj.is_private === true ? 'contained' : 'outlined'}
                                onClick={() => updateFilePrivacy(fileObj.id, true)}
                                sx={{
                                  minWidth: 'auto',
                                  px: 2,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  ...(fileObj.is_private === true ? {
                                    backgroundColor: '#f44336',
                                    '&:hover': { backgroundColor: '#d32f2f' }
                                  } : {
                                    borderColor: '#f44336',
                                    color: '#f44336',
                                    '&:hover': { borderColor: '#d32f2f', backgroundColor: 'rgba(244, 67, 54, 0.08)' }
                                  })
                                }}
                              >
                                Private
                              </Button>
                            </Box>
                          </Box>
                        )}
                        {uploadProgress[fileObj.id] && (
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={uploadProgress[fileObj.id].progress}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: uploadProgress[fileObj.id].success ? '#4caf50' : '#f44336'
                                }
                              }}
                            />
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                              {uploadProgress[fileObj.id].progress.toFixed(0)}% {uploadProgress[fileObj.id].success ? '✓' : '✗'}
                            </Typography>
                          </Box>
                        )}
                        {uploadErrors[fileObj.id] && !(uploadProgress[fileObj.id] && uploadProgress[fileObj.id].success === true) && (
                          <Box sx={{ mt: 1, p: 1, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #f44336' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.7rem', fontWeight: 500 }}>
                                ⚠️ {uploadErrors[fileObj.id]}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  if (createdJobId) {
                                    retryUpload(fileObj.id, createdJobId);
                                  } else {
                                    setShowError('Work order ID not available for retry. Please try creating the work order again.');
                                  }
                                }}
                                sx={{ 
                                  fontSize: '0.6rem', 
                                  minWidth: 'auto', 
                                  px: 1, 
                                  py: 0.5,
                                  borderColor: '#d32f2f',
                                  color: '#d32f2f',
                                  '&:hover': {
                                    backgroundColor: '#d32f2f',
                                    color: 'white'
                                  }
                                }}
                              >
                                Retry
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                      <IconButton 
                        onClick={() => removeFile(fileObj.id)}
                        size="small"
                        sx={{ color: '#dc3545' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          {/* Notes (editable by admins with client access; optional) */}
          {canEditNotesField && (
            <TextField
              label="Notes (Optional)"
              value={newJob.notes || ''}
              onChange={(e) => {
                setNewJob({ ...newJob, notes: e.target.value });
                clearFieldError('notes');
              }}
              placeholder="Internal notes (max 500 characters)"
              error={!!errors.notes}
              helperText={errors.notes}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              sx={textFieldSx}
              slotProps={{ input: { maxLength: 500 } }}
            />
          )}

        </Box>
      </DialogContent>

      {/* Submit / Actions Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        p: 3, 
        borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        backgroundColor: 'background.paper'
      }}>
        <Button 
          onClick={() => setAddJobDialogOpen(false)}
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: isDarkMode ? '#1e293b' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined"
            sx={{ 
              borderColor: isDarkMode ? '#334155' : '#e0e0e0',
              color: 'text.primary',
              '&:hover': {
                borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
              }
            }} 
            onClick={handleClear}
          >
            Clear 
          </Button>
          
          <Button 
            variant="contained"
            disabled={loading || Object.keys(errors).length}
            onClick={handleAddJob}
            sx={{ 
              backgroundColor: isDarkMode ? '#fff' : '#000', 
              color: isDarkMode ? '#000' : 'white',
              '&:hover': { 
                backgroundColor: isDarkMode ? '#e0e0e0' : '#333'
              },
              '&:disabled': {
                backgroundColor: isDarkMode ? '#475569' : '#666',
                color: isDarkMode ? '#94a3b8' : '#fff',
              }
            }}
          >
            Create
          </Button>
        </Box>
      </Box>

      {/* Modal to add a new technician */}
      <AddTechnicianDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onTechnicianAdded={handleTechnicianAdded}
        initialName={dialogValue.name}
      />

      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Work order added
        </Alert>
      </Snackbar>

      {/* Image Preview Modal */}
      <Dialog
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        maxWidth="md"
        fullWidth
        PaperComponent={PreviewPaperComponent}
        aria-labelledby="draggable-preview-title"
        PaperProps={{
          sx: {
            backgroundColor: '#000',
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        {previewImage && (
          <>
            <Box 
              id="draggable-preview-title"
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                p: 2,
                borderBottom: '1px solid #333',
                cursor: 'move'
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                {previewImage.name}
              </Typography>
              <IconButton 
                onClick={handleCloseImagePreview}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              p: 2,
              minHeight: '400px',
              backgroundColor: '#000'
            }}>
              {previewImage.type.startsWith('image/') ? (
                <img
                  src={previewImage.src}
                  alt={previewImage.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: 8
                  }}
                />
              ) : isVideoFile(previewImage.type) ? (
                <video
                  src={previewImage.src}
                  controls
                  autoPlay
                  playsInline
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    borderRadius: 8,
                    backgroundColor: '#000'
                  }}
                />
              ) : previewImage.type === 'application/pdf' ? (
                <iframe
                  src={previewImage.src}
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: 'none',
                    borderRadius: 8
                  }}
                  title={previewImage.name}
                />
              ) : previewImage.type.startsWith('text/') ? (
                <Box sx={{ 
                  width: '100%', 
                  height: '70vh', 
                  backgroundColor: '#fff', 
                  color: '#000',
                  p: 2,
                  borderRadius: 2,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  lineHeight: 1.5
                }}>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Text Preview (first 1000 characters):
                  </Typography>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {previewImage.textContent || 'Loading text content...'}
                  </pre>
                </Box>
              ) : previewImage.type.includes('word') || previewImage.type.includes('document') ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  color: 'white',
                  textAlign: 'center',
                  p: 3
                }}>
                  <DescriptionIcon sx={{ fontSize: 80, mb: 2, color: '#1976d2' }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {previewImage.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                    Microsoft Word Document
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = previewImage.src;
                      link.download = previewImage.name;
                      link.click();
                    }}
                    sx={{
                      backgroundColor: fieldSurface.accent,
                      '&:hover': { backgroundColor: isDarkMode ? '#f8fafc' : '#000000' }
                    }}
                  >
                    Download Document
                  </Button>
                  <Typography variant="caption" sx={{ opacity: 0.6, mt: 2 }}>
                    Click download to open in Microsoft Word
                  </Typography>
                </Box>
              ) : previewImage.type.includes('excel') || previewImage.type.includes('spreadsheet') ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  color: 'white',
                  textAlign: 'center',
                  p: 3
                }}>
                  <TableChartIcon sx={{ fontSize: 80, mb: 2, color: '#28a745' }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {previewImage.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                    Microsoft Excel Spreadsheet
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = previewImage.src;
                      link.download = previewImage.name;
                      link.click();
                    }}
                    sx={{
                      backgroundColor: '#28a745',
                      '&:hover': { backgroundColor: '#218838' }
                    }}
                  >
                    Download Spreadsheet
                  </Button>
                  <Typography variant="caption" sx={{ opacity: 0.6, mt: 2 }}>
                    Click download to open in Microsoft Excel
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <UploadFileIcon sx={{ fontSize: 80, mb: 2, opacity: 0.7 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {previewImage.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Preview not available for this file type
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6, mt: 1 }}>
                    {previewImage.type}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ 
              p: 2, 
              borderTop: '1px solid #333',
              backgroundColor: '#111'
            }}>
              <Typography variant="body2" sx={{ color: '#ccc', textAlign: 'center' }}>
                File size: {(previewImage.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          </>
        )}
      </Dialog>
    </Dialog>
  );
}